import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { ArrowRight, Repeat, Info } from "lucide-react"
import { PageHeader } from "@/components/portal/PageHeader"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { DatenstandHinweis } from "@/components/portal/DatenstandHinweis"

/**
 * „Wege" — welche Bedienabfolgen das Büro immer wieder geht (#44).
 *
 * Marlene am 26.08.2026: „Ist die Plattform eigentlich selbstlernend? … Das
 * wäre schon hilfreich, wenn sie wüsste welche Wege wir so gehen immer und
 * obs einfacher ginge."
 *
 * 🔑 Bewusst eine Zählung, kein Modell. Was hier auffällt, sieht ein Mensch
 * selbst — und ohne diese Zahlen hätte auch ein Modell nichts zu lesen.
 *
 * 🔴 Und bewusst mit Warnhinweis: bei ein paar hundert erfassten Klicks liest
 * man Muster hinein, die keine sind. Das Backend sagt über `belastbar`, ob die
 * Basis trägt; die Seite gibt das ungeschönt weiter, statt Zahlen zu zeigen,
 * die nach Erkenntnis aussehen.
 */

type Abfolge = { schritte: string[]; anzahl: number; benutzer?: number }
type Kennzahlen = { schritte?: number; sitzungen?: number; benutzer?: number; seiten?: number;
  /** Ohne die blossen Seitenwechsel — nur daraus entstehen Abfolgen. */
  schritte_inhaltlich?: number }
// `daten_ab` (Ticket 101): erster erfasster Schritt — reicht der Zeitraum weiter zurück, sagt die Seite es.
// Kommentar bewusst ausserhalb des Typs: tests/test_portal_wege.py liest dessen Feldnamen.
type Antwort = { kennzahlen: Kennzahlen; abfolgen: Abfolge[]; belastbar: boolean; daten_ab?: string | null }

const lesbar = (s: string) =>
  s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())

export default function WegePage() {
  const [tage, setTage] = useState(30)
  const [laenge, setLaenge] = useState(3)

  const { data, isLoading, error, refetch, isFetching } = useQuery<Antwort>({
    queryKey: ["wege-abfolgen", tage, laenge],
    queryFn: async () => {
      const r = await fetch(`/api/v1/wege/abfolgen?tage=${tage}&laenge=${laenge}`, {
        credentials: "include",
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })

  if (error) return <div className="p-10"><ErrorBox error={error} /></div>

  const k = data?.kennzahlen || {}
  const abfolgen = data?.abfolgen || []

  return (
    <div className="px-10 py-8 max-w-[1100px]">
      <PageHeader title="Wege" onRefresh={() => refetch()} refreshing={isFetching} />

      <p className="text-[13px] text-muted-foreground mb-4 max-w-[70ch]">
        Welche Abfolgen im Portal immer wieder vorkommen. Gedacht als Hinweis,
        wo sich ein Weg abkürzen ließe — gezählt wird, was bedient wurde, ohne
        Inhalte: kein Betreff, kein Kundenname.
      </p>

      <div className="flex items-center gap-3 mb-4 text-[12.5px]">
        <label className="inline-flex items-center gap-1.5">
          Zeitraum
          <select
            className="border rounded px-2 py-1 bg-transparent"
            value={tage}
            onChange={(e) => setTage(Number(e.target.value))}
          >
            <option value={7}>7 Tage</option>
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
          </select>
        </label>
        <label className="inline-flex items-center gap-1.5">
          Schritte je Abfolge
          <select
            className="border rounded px-2 py-1 bg-transparent"
            value={laenge}
            onChange={(e) => setLaenge(Number(e.target.value))}
          >
            {[2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>

      {isLoading ? (
        <Loading label="Wertet aus…" />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Kachel label="Erfasste Schritte" wert={k.schritte ?? 0} />
            <Kachel label="Sitzungen" wert={k.sitzungen ?? 0} />
            <Kachel label="Personen" wert={k.benutzer ?? 0} />
            <Kachel label="Seiten" wert={k.seiten ?? 0} />
          </div>

          <DatenstandHinweis datenAb={data?.daten_ab} tage={tage} />

          {/* 🔴 Der ehrliche Teil: sagen, wenn die Basis noch nicht trägt. */}
          {!data?.belastbar && (
            <div className="flex items-start gap-2 p-3 mb-4 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 text-[12.5px]">
              <Info className="w-4 h-4 shrink-0 mt-[1px] text-amber-700 dark:text-amber-400" />
              <span>
                <strong>Noch zu wenig Daten für belastbare Aussagen.</strong>{" "}
                Von {k.schritte ?? 0} erfassten Schritten sind{" "}
                {k.schritte_inhaltlich ?? 0} inhaltliche Bedienschritte — der
                Rest sind Seitenwechsel, aus denen sich kein Weg ablesen lässt.
                Ab etwa 500 inhaltlichen Schritten wird aus Zufall ein Muster.
                Was unten steht, ist eine Momentaufnahme — keine Empfehlung.
              </span>
            </div>
          )}

          {abfolgen.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-[13px] border rounded-md">
              Für diesen Zeitraum sind keine Abfolgen mit {laenge} Schritten erfasst.
            </div>
          ) : (
            <div className="border rounded-md overflow-hidden">
              {abfolgen.map((a, i) => {
                // 🔴 Gürtel UND Hosenträger (#61): die Antwort liefert
                // `schritte` — als sie es einmal nicht tat, beendete
                // `undefined.length` die ganze Seite, statt eine Zeile
                // auszulassen. Der Vertrag steht jetzt im Backend und in
                // `tests/test_portal_wege.py`; hier steht nur noch, dass
                // eine Lücke die Seite nicht mitreißt.
                const schritte = a.schritte ?? []
                const wiederholung = schritte.length > 1
                  && schritte.every((s) => s === schritte[0])
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 text-[13px]"
                  >
                    <span className="text-[11px] text-muted-foreground w-6 shrink-0">{i + 1}.</span>
                    <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
                      {schritte.map((s, j) => (
                        <span key={j} className="inline-flex items-center gap-1.5">
                          {j > 0 && <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />}
                          <span className="px-1.5 py-0.5 rounded bg-secondary">{lesbar(s)}</span>
                        </span>
                      ))}
                      {/* Derselbe Schritt mehrfach hintereinander ist der
                          interessanteste Fall: genau so entstand #42 (fünfmal
                          derselbe Anhang-Dialog). */}
                      {wiederholung && (
                        <span
                          className="inline-flex items-center gap-1 text-[11.5px] text-amber-700 dark:text-amber-400"
                          title="Derselbe Schritt mehrfach hintereinander — hier könnte eine Sammelaktion helfen"
                        >
                          <Repeat className="w-3 h-3" /> Wiederholung
                        </span>
                      )}
                    </div>
                    <span className="text-[12px] text-muted-foreground shrink-0 tabular-nums">
                      {a.anzahl}×
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Kachel({ label, wert }: { label: string; wert: number }) {
  return (
    <div className="border rounded-md px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-[19px] font-semibold tabular-nums">{wert}</div>
    </div>
  )
}
